import { useInfiniteQuery } from "@tanstack/react-query";
import { useCallback } from "react";
import { boronClient } from "../../api";
import { createError, HTTPErrors } from "../../errors";
import { getNextPageParam } from "../../helpers/ReactQueryHelper";
import ApiResponse from "../../interfaces/ApiResponse";
import { CommentInterface } from "../../interfaces/CommentInterface";

type Props = {
  sectionId: string;
  enabled?: boolean;
};
const URL = "/api/v1/sections/{section_id}/study_records/comments";

export const useFetchStudyRecordComments = ({
  sectionId,
  enabled = true,
}: Props) => {
  const query = useInfiniteQuery<ApiResponse<CommentInterface[]>, HTTPErrors>({
    queryKey: [URL, sectionId],
    queryFn: async ({ pageParam }) => {
      const { response, data } = await boronClient.GET(URL, {
        params: {
          path: {
            section_id: sectionId,
          },
          query: {
            page: pageParam,
          },
        },
      });

      if (response.ok && data) {
        return data.studyRecordComments;
      }

      throw await createError(response);
    },
    initialPageParam: 1,
    getNextPageParam,
    enabled,
  });

  const loadMore = useCallback(() => {
    if (!query.isFetchingNextPage && query.hasNextPage) {
      query.fetchNextPage();
    }
  }, [query.isFetchingNextPage, query.hasNextPage, query.fetchNextPage]);

  const allComments = query.data?.pages.flatMap((page) => page.data);

  return {
    loadMore,
    ...query,
    allComments,
  };
};
